import {AmbientErrorBanner} from '@github-ui/copilot-chat/components/AmbientErrorBanner'
import {QuotaBanner} from '@github-ui/copilot-chat/components/quota/QuotaBanner'
import {useQuotaBanner} from '@github-ui/copilot-chat/hooks/use-quota-banner'
import {useSelectedCopilotSpaceId} from '@github-ui/copilot-chat/hooks/use-selected-custom-copilot-id'
import {useChatStateValues} from '@github-ui/copilot-chat/utils/CopilotChatContext'
import {useEntitlement} from '@github-ui/copilot-entitlement/EntitlementContext'
import {TransactionalMessageBanners} from '@github-ui/in-product-targeting'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'

import type {CopilotImmersivePayload} from '../routes/payloads'
import styles from './NotificationsSection.module.css'
import {FreeChatExceededBanner} from './Quota/FreeChatExceededBanner'
import {QuotaExceededBanner, useQuotaExceededBannerVisibility} from './Quota/QuotaExceededBanner'
import {QuotaMeterBanner, useQuotaMeterBannerVisibility} from './Quota/QuotaMeterBanner'
import {SpaceDeletedBanner, SpaceSizeExceededBanner} from './Spaces/SpaceBanners'
import {spaceConversationVariants, useSpaceConversationStatus} from './Spaces/SpaceConversationBanner'
import {findMatchingSsoOrg, SsoAuthBanner} from './SsoAuthBanner'

interface NotificationsSectionProps {
  inputText?: string
}

export function NotificationsSection({inputText = ''}: NotificationsSectionProps = {}) {
  const {ambientError, selectedThreadID, ssoOrganizations} = useChatStateValues(
    'ambientError',
    'selectedThreadID',
    'ssoOrganizations',
  )
  const {transactionalMessageBanner} = useAppPayload<CopilotImmersivePayload>()
  const customCopilotId = useSelectedCopilotSpaceId()
  const {chatQuotaExceeded, isLicensedLimited, tokenBasedBillingEnabled} = useEntitlement()
  const quotaBanner = useQuotaBanner()
  const {variant, customCopilot} = useSpaceConversationStatus(customCopilotId, selectedThreadID)
  const customCopilotExists = Boolean(customCopilot)

  const canUseChat = tokenBasedBillingEnabled
    ? (!variant || customCopilotExists) && !(quotaBanner.show && quotaBanner.isBlocking)
    : (!variant || customCopilotExists) && !(isLicensedLimited && chatQuotaExceeded)
  const quotaVisibility = useQuotaMeterBannerVisibility()
  const quotaExceededVisibility = useQuotaExceededBannerVisibility()

  const showSpaceDeletedBanner = variant === spaceConversationVariants.customCopilotDisabled
  const showSpaceSizeExceededBanner = variant === spaceConversationVariants.customCopilotSizeExceeded
  const showAmbientErrorBanner = canUseChat && ambientError
  const showQuotaBanner = quotaBanner.show
  const showQuotaExceededBanner = canUseChat && quotaExceededVisibility.visible
  const showQuotaMeterBanner = canUseChat && quotaVisibility.visible
  const showFreeChatExceededBanner = tokenBasedBillingEnabled ? false : !canUseChat
  const showSpaceErrorBanners = showSpaceDeletedBanner || showSpaceSizeExceededBanner
  const transactionalMessageBanners = transactionalMessageBanner ?? []
  const showTransactionalMessageBanner = transactionalMessageBanners.length > 0
  const matchingSsoOrg =
    canUseChat && !showSpaceDeletedBanner && !showSpaceSizeExceededBanner
      ? findMatchingSsoOrg(inputText, ssoOrganizations)
      : null
  const showSsoAuthBanner = Boolean(matchingSsoOrg)

  const showSection =
    showTransactionalMessageBanner ||
    showSpaceDeletedBanner ||
    showSpaceSizeExceededBanner ||
    showAmbientErrorBanner ||
    showQuotaBanner ||
    showQuotaExceededBanner ||
    showQuotaMeterBanner ||
    showFreeChatExceededBanner ||
    showSsoAuthBanner

  return showSection ? (
    <section aria-label="Notifications" className={styles.notificationsSection}>
      {showTransactionalMessageBanner ? (
        <div className={styles.banner}>
          <TransactionalMessageBanners banners={transactionalMessageBanners} displayStyle="inset" />
        </div>
      ) : null}
      {showSpaceErrorBanners ? (
        <div className={styles.spaceBannerContainer}>
          {showSpaceDeletedBanner ? <SpaceDeletedBanner /> : null}
          {showSpaceSizeExceededBanner ? <SpaceSizeExceededBanner customCopilot={customCopilot} /> : null}
        </div>
      ) : null}
      {showAmbientErrorBanner ? <AmbientErrorBanner ambientError={ambientError} useLargerBorderRadius /> : null}
      <div role="status">{matchingSsoOrg ? <SsoAuthBanner matchingOrg={matchingSsoOrg} /> : null}</div>
      {/* The user cannot chat if the space is deleted or the size is exceeded so we don't need to show additional banners in that case. */}
      {!showSpaceErrorBanners ? (
        <>
          {!showQuotaBanner && showQuotaExceededBanner ? (
            <QuotaExceededBanner onDismiss={quotaExceededVisibility.onDismiss} />
          ) : null}
          {showQuotaBanner ? <QuotaBanner {...quotaBanner} /> : null}
          {showQuotaMeterBanner ? (
            <QuotaMeterBanner className={styles.banner} onDismiss={quotaVisibility.onDismiss} />
          ) : null}
          {showFreeChatExceededBanner ? <FreeChatExceededBanner /> : null}
        </>
      ) : null}
    </section>
  ) : null
}
